/**
 * Scan → display representation map for the AI summary request (STORY-011 / #719).
 *
 * Syphon's `POST /scans/summarize` takes the HUMAN-READABLE form of every scan
 * name and value — never a raw Syphon code. `MarketCap` `52340` goes over the
 * wire as `時価総額` / `52,340億円`, Exchange `166,0111` as `取引所` / `東証プライム`.
 * This file is the single source of truth for that conversion.
 *
 * Config-only by design, mirroring `hashtagFilterConfig.js`: changing how a
 * value renders in the summary is an edit here, not a logic change.
 *
 * IMPORTANT: every `value` Syphon receives must be a STRING (or an array of
 * strings for the `in` operator). The service 400s on numbers — see
 * `validateHashtags` in syphon `src/scans/scansService.js`.
 */

/**
 * Recursively freezes the config tree so a consumer cannot mutate a shared
 * entry. Same helper shape as `hashtagFilterConfig.js`.
 */
function deepFreeze(obj) {
  Object.values(obj).forEach((value) => {
    if (value && typeof value === 'object' && !Object.isFrozen(value)) {
      deepFreeze(value);
    }
  });
  return Object.freeze(obj);
}

/**
 * Popsicle filter operator → Syphon operator.
 *
 * Syphon's whitelist is `['in', '>=', '<=', '>', '<', '=']` (`VALID_OPERATORS`);
 * anything else is a `400 INVALID_REQUEST`.
 *
 * `crossoverWithinDays` maps to `<=` because the hashtag `value` is a MAXIMUM
 * age in days: `hashtagFilterEngine.js` compares `daysAgo <= value` against the
 * derived `CrossoverDaysAgo` field.
 *
 * `notInCodeSet` has NO Syphon equivalent and is deliberately absent. It is
 * handled as a per-condition special case (see `POSITIVE_FORM` below), NOT by a
 * general negation rule.
 */
export const OPERATOR_MAP = Object.freeze({
  '>=': '>=',
  '<=': '<=',
  '>': '>',
  '<': '<',
  '=': '=',
  inCodeSet: 'in',
  stringEquals: '=',
  crossoverWithinDays: '<='
});

/**
 * Conditions whose popsicle form is a NEGATION but whose display form has a
 * named positive equivalent, letting them use Syphon's existing operators.
 *
 * `SecurityType notInCodeSet ['0115','0116','0119']` excludes ETF/ETF/REIT —
 * which is exactly "common stock". The upstream ScannerCategory option is named
 * `Equity` (`^(?!0115|0116|0119).*$`), so `証券種類 = 普通株` is the accurate
 * display form, not a workaround.
 *
 * Keyed by `${scanName}:${operator}`. If a future hashtag adds a
 * `notInCodeSet` with no named complement, it will not be found here and the
 * assembler drops the condition rather than sending an invalid operator.
 */
export const POSITIVE_FORM = deepFreeze({
  'SecurityType:notInCodeSet': {
    operator: '=',
    valueKey: 'scan.criteria.SecurityType.Equity'
  }
});

/**
 * Per-scan display config.
 *
 * - `labelKey`   localization key for the JP metric name (the `label` field).
 *                Note the two namespaces: some scans live under
 *                `desktop.scans.criteria.*` and others under `scan.criteria.*`.
 * - `unit`       suffix appended to a numeric value. Empty string = no unit.
 * - `matchedUnit` overrides `unit` for a symbol's ACTUAL value, where the
 *                threshold's phrasing would misread. Only `SMACrossover_5_25`
 *                needs it: `10日以内` is a correct threshold but `3日以内` is a
 *                wrong reading of "crossed over 3 days ago".
 * - `decimals`   max decimal places; trailing zeros are trimmed.
 * - `grouped`    thousands separators (`52,340億円`).
 * - `optionPrefix` present for CODE-valued scans: the value is a code, and its
 *                display label is `optionPrefix + code`. Mutually exclusive
 *                with `unit`/`decimals`.
 * - `zeroIsAbsent` for metrics where `0` cannot co-exist with the metric being
 *                meaningful, so a zero is missing data rather than a real
 *                value. Syphon's `filterValue` uses `0` for both, and a `0`
 *                often SATISFIES the hashtag threshold (`配当性向 <= 30`), so
 *                such symbols reach `matched[]` and would tell the LLM a false
 *                fact. Omitted instead, per AC C19978731. NOT set where zero is
 *                genuine — `SMADeviation_25` at the average, `NetSalesChange`
 *                flat, `PredictedOrdinaryProfit` at breakeven (its own
 *                threshold is `>= 0`), or a real low score.
 * - `codeGroups` / `codeGroupDefault` for code-valued scans whose string table
 *                is keyed by GROUP rather than by raw code — the code is
 *                bucketed before the lookup.
 */
const SCAN_DISPLAY = deepFreeze({
  // --- Code-valued scans (value is a code, resolved via optionPrefix) --------
  Sector: {
    labelKey: 'desktop.scans.criteria.Sector',
    optionPrefix: 'scan.criteria.Sector.'
  },
  // A symbol's raw value is a SectionClassCode (`0111`, `0115`, …), but the
  // string table is keyed by TYPE (`.Equity` / `.ETF` / `.REIT`) — so the code
  // is bucketed first. Same partition `SECURITY_TYPE_CODES_NON_EQUITY` encodes
  // for filtering: everything that is not an ETF/REIT code is common stock.
  SecurityType: {
    labelKey: 'desktop.scans.criteria.SecurityType',
    optionPrefix: 'scan.criteria.SecurityType.',
    codeGroups: { '0115': 'ETF', '0116': 'ETF', '0119': 'REIT' },
    codeGroupDefault: 'Equity'
  },
  IchimokuCloud: {
    labelKey: 'scan.criteria.IchimokuCloud',
    optionPrefix: 'scan.criteria.IchimokuCloud.'
  },
  // Exchange is code-valued but its filterValue is the COMPOUND
  // `<Listed_Exchange>,<SectionClassCode>` (e.g. `166,0111`). The assembler
  // splits on the comma and resolves the section against this prefix, using the
  // `.long` variant so the summary reads `東証プライム`, not the `東P` short form.
  Exchange: {
    labelKey: 'desktop.scans.criteria.Exchange',
    optionPrefix: 'exchange.TSE.',
    optionSuffix: '.long',
    compound: true
  },

  // --- Currency -------------------------------------------------------------
  // Syphon's filterValue is already in 億円 (hashtagFilterConfig stores
  // thresholds in the SAME unit — `5000` there means ¥500B).
  MarketCap: {
    zeroIsAbsent: true,
    labelKey: 'desktop.scans.criteria.MarketCap',
    unit: '億円',
    decimals: 2,
    grouped: true
  },
  // Raw yen, unlike MarketCap — see the `// raw yen` note on hashtag #4.
  InvestmentAmount: {
    zeroIsAbsent: true,
    labelKey: 'desktop.scans.criteria.InvestmentAmount',
    unit: '円',
    decimals: 0,
    grouped: true
  },
  PredictedOrdinaryProfit: {
    labelKey: 'scan.criteria.PredictedOrdinaryProfit',
    unit: '億円',
    decimals: 2,
    grouped: true
  },

  // --- Percentages ----------------------------------------------------------
  DividendYieldForecast: {
    zeroIsAbsent: true,
    labelKey: 'scan.criteria.DividendYieldForecast',
    unit: '%',
    decimals: 2
  },
  DividendPayoutRatio: {
    zeroIsAbsent: true,
    labelKey: 'scan.criteria.DividendPayoutRatio',
    unit: '%',
    decimals: 1
  },
  ReturnOnEquity: {
    labelKey: 'scan.criteria.ReturnOnEquity',
    unit: '%',
    decimals: 1
  },
  OrdinaryProfitToNetSalesRatio: {
    labelKey: 'desktop.scans.criteria.OrdinaryProfitToNetSalesRatio',
    unit: '%',
    decimals: 1
  },
  NetSalesChange: {
    labelKey: 'desktop.scans.criteria.NetSalesChange',
    unit: '%',
    decimals: 1
  },
  TargetPriceVsCurrentPrice: {
    labelKey: 'scan.criteria.TargetPriceVsCurrentPrice',
    unit: '%',
    decimals: 1
  },
  SMADeviation_25: {
    labelKey: 'scan.criteria.SMADeviation_25',
    unit: '%',
    decimals: 1
  },
  StochasticFast_9: {
    labelKey: 'scan.criteria.StochasticFast_9',
    unit: '%',
    decimals: 1
  },

  // --- Multiples (倍) -------------------------------------------------------
  PriceCashFlowRatio: {
    zeroIsAbsent: true,
    labelKey: 'scan.criteria.PriceCashFlowRatio',
    unit: '倍',
    decimals: 2
  },
  InventoryTurnoverRate: {
    zeroIsAbsent: true,
    labelKey: 'scan.criteria.InventoryTurnoverRate',
    unit: '倍',
    decimals: 1
  },
  ReceivablesTurnoverRate: {
    zeroIsAbsent: true,
    labelKey: 'scan.criteria.ReceivablesTurnoverRate',
    unit: '倍',
    decimals: 1
  },

  // --- Unitless scores ------------------------------------------------------
  // Rating is a 1–5 consensus figure; a unit would misread as a percentage.
  Rating: {
    labelKey: 'scan.criteria.Rating',
    unit: '',
    decimals: 1,
    zeroIsAbsent: true
  },
  TotalScore: { labelKey: 'scan.criteria.TotalScore', unit: '', decimals: 0 },
  GrowthScore: { labelKey: 'scan.criteria.GrowthScore', unit: '', decimals: 0 },
  AnalystCount: {
    labelKey: 'scan.criteria.AnalystCount',
    unit: '',
    decimals: 0
  },

  // --- Derived --------------------------------------------------------------
  // Paired with `crossoverWithinDays` → `<=`, so the value reads as a window.
  SMACrossover_5_25: {
    labelKey: 'scan.criteria.SMACrossover_5_25',
    unit: '日以内',
    matchedUnit: '日前',
    decimals: 0
  }
});

export default SCAN_DISPLAY;
